import { useCallback, useMemo, useRef, useState } from 'react'

import { Modal } from './modal'
import { ConfirmModalContext, ConfirmModalOptions, ConfirmResult } from './use-confirm-modal'

export function ConfirmModalProvider({ children }: { children: React.ReactNode }) {
  const [modalProps, setModalProps] = useState<ConfirmModalOptions | null>(null)
  const resolveRef = useRef<((value: ConfirmResult) => void) | null>(null)

  const close = useCallback((result: ConfirmResult) => {
    resolveRef.current?.(result)
    resolveRef.current = null
    setModalProps(null)
  }, [])

  const confirm = useCallback((options: ConfirmModalOptions) => {
    return new Promise<ConfirmResult>((resolve) => {
      resolveRef.current = resolve
      setModalProps(options)
    })
  }, [])

  // Sits at the app root, so a fresh value object would re-render every consumer on open/close.
  const value = useMemo(() => ({ confirm }), [confirm])

  return (
    <ConfirmModalContext.Provider value={value}>
      {children}
      <Modal
        {...modalProps}
        open={modalProps !== null}
        onOk={async () => close(ConfirmResult.CONFIRMED)}
        onCancel={() => close(ConfirmResult.CANCELLED)}
        onDismiss={() => close(ConfirmResult.DISMISSED)}
        onCancelButton={() => close(ConfirmResult.CANCELLED)}
      >
        {modalProps?.content}
      </Modal>
    </ConfirmModalContext.Provider>
  )
}
